import { useState } from "react";
import { useLocation } from "wouter";
import { GetAllVideoInsightsReq } from "@/api/studio/insights/api";
import { useGetAllVideoInsights, useGetVideoStudySummary } from "@/api/studio/insights/queries";
import { useGetVideoBenchmark } from "@/api/studio/library/queries";
import { getBenchmarkComparison } from "@/api/studio/library/utils/get-benchmark-comparison";
import { ReportsLayout } from "@/components/layouts";
import { SelectSortVideoReports } from "@/components/ui/select";
import { VideoBenchmarkChart } from "@/features/reports/components/video-benchmark-chart";
import { VideoCopilotSummaryHeader } from "@/features/reports/components/video-copilot-summary-header";
import { VideoReportCard } from "@/features/reports/components/video-report-card";
import { VideoReportsSkeleton } from "@/features/reports/components/video-reports-skeleton";
import { getTopScore } from "@/features/reports/utils/get-top-score";

/* Prop Types */
interface ReportsVideoOverviewRouteProps {
  projectId: string;
  studyId: string;
}

/**
 * ReportsVideoOverviewRoute component
 *
 * Fetches video attention insights for a study and renders a VideoReportCard per video,
 * with a sort toolbar that reorders the cards via the insights query.
 */
export const ReportsVideoOverviewRoute = ({ projectId, studyId }: ReportsVideoOverviewRouteProps) => {
  /* State */
  // The sort toolbar drives these; defaults match the backend defaults
  const [orderBy, setOrderBy] = useState<GetAllVideoInsightsReq["orderBy"]>("attention");
  const [sortBy, setSortBy] = useState<GetAllVideoInsightsReq["sortBy"]>("desc");

  /* Stores */
  const [, navigate] = useLocation();

  /* Queries */
  const { data: insights, isLoading } = useGetAllVideoInsights({
    projectId,
    studyId,
    orderBy,
    sortBy,
  });
  const { data: benchmark } = useGetVideoBenchmark({ projectId, studyId });
  const { data: studySummary } = useGetVideoStudySummary({ projectId, studyId });

  /* Render */
  return (
    <ReportsLayout projectId={projectId} studyId={studyId} hideSidebar>
      {/* Loading State */}
      {isLoading && <VideoReportsSkeleton />}

      {/* Loaded Content */}
      {!isLoading && insights && insights.length > 0 && (
        <div className="grow grid grid-cols-1 gap-4 overflow-y-auto">
          <VideoCopilotSummaryHeader projectId={projectId} studyId={studyId} summary={studySummary?.summary ?? ""} />

          <div className="grid grid-cols-[1fr_420px] gap-4">
            <h1 className="text-4xl px-4">{studySummary?.summary ?? ""}</h1>
          </div>

          {benchmark && <VideoBenchmarkChart insights={insights} benchmark={benchmark} projectId={projectId} studyId={studyId} />}

          <div className="divider"></div>
          {/* Toolbar */}
          <div className="flex justify-between items-center px-4">
            <p className="font-semibold">Assets</p>
            <SelectSortVideoReports orderBy={orderBy} sortBy={sortBy} setOrderBy={setOrderBy} setSortBy={setSortBy} />
          </div>

          {/* Cards */}
          <div className="flex gap-4 items-start px-4">
            {insights.map((insight, index) => (
              <VideoReportCard
                key={insight.assetId}
                insight={insight}
                isTopScore={getTopScore(index, insights.length, sortBy)}
                projectId={projectId}
                studyId={studyId}
                benchmarkComparison={benchmark ? getBenchmarkComparison(insight.creative.attention.score, benchmark) : undefined}
                onClick={() => navigate(`/projects/${projectId}/studies/${studyId}/video/reports/in-depth/${insight.key}`)}
              />
            ))}
          </div>
        </div>
      )}

      {/* Empty State */}
      {!isLoading && (!insights || insights.length === 0) && (
        <div className="flex items-center justify-center h-full">
          <div className="text-center">
            <p className="text-base-content/70">No videos found for this study.</p>
          </div>
        </div>
      )}
    </ReportsLayout>
  );
};
